import React, { useState, useEffect, useCallback, useContext, Dispatch, SetStateAction, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider as CustomThemeProvider } from "@/contexts/ThemeContext";
import { PaymentProvider } from "@/contexts/PaymentContext";
import { StatusProvider } from "@/contexts/StatusContext";
import { ProtectedRoute } from "@/components/consultant/auth/ProtectedRoute";
import { useIsMobile } from "@/hooks/use-mobile";
import IridescenceBackground from "@/pages/landing/components/IridescenceBackground";

// Lazy load all pages for better performance
const Calendar = lazy(() => import("./pages/consultant/Calendar"));
const CreateProject = lazy(() => import("./pages/manager/CreateProject"));
const AppLayout = lazy(() => import("./components/layout/AppLayout"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const GlobalAIAssistant = lazy(() => import("./components/consultant/GlobalAIAssistant"));
const Dashboard = lazy(() => import("./pages/consultant/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Contacts = lazy(() => import("./pages/Contacts"));
const NotesWorkspace = lazy(() => import("./pages/NotesWorkspace"));
const Research = lazy(() => import("./pages/consultant/Research"));
const ProjectOverview = lazy(() => import("./pages/consultant/ProjectOverview"));
const ProjectFiles = lazy(() => import("./pages/consultant/ProjectFiles"));
const ProjectChat = lazy(() => import("./pages/consultant/ProjectChat"));
const ProjectMapPage = lazy(() => import("@/components/consultant/ProjectMap").then(module => ({ default: module.ProjectMapPage })));
const ProjectManagement = lazy(() => import("./pages/consultant/ProjectManagement"));
const ClientPortalDashboard = lazy(() => import('./pages/clientportal/Dashboard'));
const ManagerDashboard = lazy(() => import('./pages/manager/Dashboard'));
const ManagerProjects = lazy(() => import('./pages/manager/Projects'));
const Billing = lazy(() => import('./pages/Billing'));
const UsageTracking = lazy(() => import('./pages/UsageTracking'));
const BillingReturn = lazy(() => import('./pages/BillingReturn'));
const ClientPortalChat = lazy(() => import('./pages/clientportal/Chat'));
const Admin = lazy(() => import('./pages/admin/Admin'));
const Clients = lazy(() => import('./pages/manager/Clients'));
const BusinessDashboard = lazy(() => import('./pages/business/BusinessDashboard'));
const BusinessChat = lazy(() => import('./pages/business/BusinessChat'));
const BusinessContacts = lazy(() => import('./pages/business/BusinessContacts'));
const BusinessAppointments = lazy(() => import('./pages/business/BusinessAppointments'));
const BusinessChatbotSettings = lazy(() => import('./pages/business/BusinessChatbotSettings'));
const BusinessOrders = lazy(() => import("@/pages/business/BusinessOrders"));
const BusinessReservations = lazy(() => import("@/pages/business/BusinessReservations"));
const BusinessClient = lazy(() => import('./pages/business/BusinessClient'));
const Docs = lazy(() => import('./pages/Docs'));

// Landing page components - lazy loaded for performance but no loading state
const Landing = lazy(() => import('./pages/landing/Landing'));
const Terms = lazy(() => import('./pages/landing/Terms'));
const Privacy = lazy(() => import('./pages/landing/Privacy'));
const About = lazy(() => import('./pages/landing/About'));
const Pricing = lazy(() => import('./pages/landing/Pricing'));
const Contact = lazy(() => import('./pages/landing/Contact'));
const Features = lazy(() => import('./pages/landing/Features'));
const Integrations = lazy(() => import('./pages/landing/Integrations'));

const NotFound = lazy(() => import('./pages/NotFound'));
const CompanySelection = lazy(() => import('./pages/CompanySelection'));
const RoleSelection = lazy(() => import('./pages/RoleSelection'));
const BusinessTypeSelection = lazy(() => import('./pages/BusinessTypeSelection'));
const CompanyManagement = lazy(() => import('./pages/CompanyManagement'));
const DataDeletion = lazy(() => import('./pages/DataDeletion'));

// Landing layout wrapper to preserve header/footer during navigation
const LandingLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <LandingHeader />
      <div className="min-h-screen">
        {children}
      </div>
      <LandingFooter />
      <CookieConsent />
    </>
  );
};

// Import LandingHeader and LandingFooter for the layout
import LandingHeader from './pages/landing/components/LandingHeader';
import LandingFooter from './pages/landing/components/LandingFooter';

// Non-lazy imports (needed immediately)
import CompanyRedirect from './components/CompanyRedirect';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import ErrorBoundary from '@/components/ui/error-boundary';
import { NetworkStatus } from '@/components/ui/enhanced-loading';
import OnboardingTour from '@/components/onboarding/OnboardingTour';
import { Toaster } from "@/components/ui/toaster";
import WheelMenu, { WheelMenuItem } from "@/components/consultant/WheelMenu";
import CookieConsent from "@/components/ui/cookie-consent";
import { CalendarPlus, ListTodo, StickyNote, Settings as SettingsIcon, Timer, Search, Upload, Link, Share2, Languages, Mic, Video, Settings, Bot as AiIcon, Home, Users, MessageSquare, Folder, BookOpen, BotMessageSquare, FileVideo, Table, Map, SquareGanttChart, BookOpenText, BookText, Mail, UserCircle, Receipt, Bell, CreditCard, FileStack, LayoutGrid, Network } from 'lucide-react';
import WheelMenuSettings, { WheelMenuSettingsData } from "@/components/consultant/WheelMenuSettings";
import { supabase } from '@/integrations/supabase/client';

// Check if running in Tauri
const isTauri = () => {
  return typeof window !== 'undefined' && !!(window as any).__TAURI_INTERNALS__;
};

// Smart Root redirect component - redirects to auth for Tauri, landing for web
const RootRedirect = () => {
  // Check if this is an email confirmation redirect (has hash fragments with auth tokens)
  const hash = window.location.hash;
  const hasAuthTokens = hash.includes('access_token') && hash.includes('type=');

  if (isTauri() || hasAuthTokens) {
    return <Navigate to="/auth" replace />;
  }
  return <Navigate to="/landing" replace />;
};

// Helper function to get business dashboard path based on company's business_type
const getBusinessDashboardPath = async (userId: string): Promise<string> => {
  try {
    // Get user's company_id from profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', userId)
      .single();

    if (profileError || !profile?.company_id) {
      return '/business/other/dashboard';
    }

    // Get company's business_type
    const { data: company, error: companyError } = await supabase
      .from('companies')
      .select('business_type')
      .eq('id', profile.company_id)
      .single();

    if (companyError || !company) {
      return '/business/other/dashboard';
    }

    // Validate business_type and return appropriate path
    const businessType = (company as { business_type: string | null })?.business_type;
    if (!businessType) {
      return '/business/other/dashboard';
    }
    const validTypes = ['medical', 'hospitality', 'beauty', 'fitness', 'shop', 'other'];
    const mappedType = validTypes.includes(businessType) ? businessType : 'other';
    
    return `/business/${mappedType}/dashboard`;
  } catch (error) {
    console.error('Error fetching business dashboard path:', error);
    return '/business/other/dashboard';
  }
};

// Smart PWA redirect component
const PWARedirect = () => {
  const { user, profile, isInitialized } = useAuth();
  const [isLoading, setIsLoading] = React.useState(true);
  const [redirectPath, setRedirectPath] = React.useState<string | null>(null);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const initializeRedirect = async () => {
      // Wait for auth to be fully initialized
      if (!isInitialized) {
        return;
      }

      if (!user) {
        // Check if this might be a PWA launch and wait a bit for auth to initialize
        const isPWA = window.matchMedia('(display-mode: standalone)').matches;
        if (isPWA) {
          // Wait a bit longer for auth to initialize in PWA mode
          timeoutId = setTimeout(() => {
            if (isMountedRef.current) {
              setIsLoading(false);
            }
          }, 2000);
        } else {
          setIsLoading(false);
        }
        return;
      }

      // Get user role from profile
      const userRole = profile?.role || null;

      // If business user, fetch the correct dashboard path
      if (userRole === 'business') {
        const path = await getBusinessDashboardPath(user.id);
        if (isMountedRef.current) {
          setRedirectPath(path);
        }
      } else {
        // For other roles, set path immediately
        switch (userRole) {
          case 'manager':
            setRedirectPath('/manager/dashboard');
            break;
          case 'client':
            setRedirectPath('/clientportal/dashboard');
            break;
          case 'consultant':
          default:
            setRedirectPath('/consultant/dashboard');
            break;
        }
      }

      setIsLoading(false);
    };

    initializeRedirect();

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [user, profile, isInitialized]);

  // Cleanup effect to mark component as unmounted
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (isLoading || !redirectPath) {
    return <LoadingSpinner size="md" />;
  }

  if (!user) {
    // Check if user has a saved session preference
    const lastRole = localStorage.getItem('lastUserRole');
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;

    if (isPWA && lastRole) {
      // If launched from PWA and we have a saved role, try to go to login with intent to redirect
      return <Navigate to={`/auth?redirect=${encodeURIComponent(`/${lastRole}/dashboard`)}`} replace />;
    }

    return <Navigate to="/" replace />;
  }

  // Get user role from profile
  const userRole = profile?.role || null;

  // Save user role for future PWA launches
  if (userRole) {
    localStorage.setItem('lastUserRole', userRole);
  }

  // If user doesn't have a role, redirect to role selection flow
  if (!userRole) {
    // If offline or we encountered transient errors fetching role, avoid redirecting away
    if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
      return <LoadingSpinner size="md" />;
    }
    return <Navigate to="/role-selection" replace />;
  }

  // Redirect to the determined path
  return <Navigate to={redirectPath} replace />;
};

import { ProjectsProvider, useProjects } from "@/contexts/ProjectsContext";
import { NavigationProvider } from "@/contexts/NavigationContext";
import PWAInstallPrompt from "@/components/ui/pwa-install-prompt";
import { GlobalDialogProvider, useGlobalDialog } from "@/contexts/GlobalDialogContext";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { useProjectCountTracking } from "@/hooks/useProjectCountTracking";

// New ScrollToTop Component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Define default/initial settings
const defaultWheelSettings: WheelMenuSettingsData = {
  hotkey: 'g',
  selectedItemIds: ['ai-assistant', 'event', 'task', 'note', 'nav-dashboard', 'nav-contacts', 'nav-chatbot'], // Include quick navigation actions by default
  holdMode: false
};

// Define the map of all possible actions OUTSIDE the component
const allWheelActions: Record<string, Omit<WheelMenuItem, 'id'> & { id: string }> = {
  // Quick actions
  'ai-assistant': { id: 'ai-assistant', label: 'AI Assistant', icon: <AiIcon size={20} />, action: () => { } },
  event: { id: 'event', label: 'Create Event', icon: <CalendarPlus size={20} />, action: () => { } },
  task: { id: 'task', label: 'New Task', icon: <ListTodo size={20} />, action: () => { } },
  note: { id: 'note', label: 'Add Note', icon: <StickyNote size={20} />, action: () => { } },
  settings: { id: 'settings', label: 'Open Settings', icon: <SettingsIcon size={20} />, action: () => { } },

  // Quick navigation - Core pages
  'nav-dashboard': { id: 'nav-dashboard', label: 'Dashboard', icon: <Home size={20} />, action: () => { } },
  'nav-calendar': { id: 'nav-calendar', label: 'Calendar', icon: <CalendarPlus size={20} />, action: () => { } },
  'nav-notes': { id: 'nav-notes', label: 'Notes', icon: <StickyNote size={20} />, action: () => { } },
  'nav-projects': { id: 'nav-projects', label: 'Projects', icon: <Folder size={20} />, action: () => { } },
  'nav-notifications': { id: 'nav-notifications', label: 'Notifications', icon: <Bell size={20} />, action: () => { } },
  'nav-profile': { id: 'nav-profile', label: 'Profile', icon: <UserCircle size={20} />, action: () => { } },
  'nav-billing': { id: 'nav-billing', label: 'Billing', icon: <CreditCard size={20} />, action: () => { } },

  // Contacts & Messaging
  'nav-contacts': { id: 'nav-contacts', label: 'Contacts', icon: <Users size={20} />, action: () => { } },
  'nav-messages': { id: 'nav-messages', label: 'Synkro Chat', icon: <MessageSquare size={20} />, action: () => { } },
  'nav-email': { id: 'nav-email', label: 'Email Client', icon: <Mail size={20} />, action: () => { } },

  // Research Tools (specific tools instead of generic research)
  'nav-chatbot': { id: 'nav-chatbot', label: 'AI Chatbot', icon: <BotMessageSquare size={20} />, action: () => { } },
  'nav-insights': { id: 'nav-insights', label: 'Video Insights', icon: <FileVideo size={20} />, action: () => { } },
  'nav-dataframes': { id: 'nav-dataframes', label: 'Dataframes', icon: <Table size={20} />, action: () => { } },
  'nav-speedreader': { id: 'nav-speedreader', label: 'Speed Reader', icon: <BookOpen size={20} />, action: () => { } },
  'nav-mapmaker': { id: 'nav-mapmaker', label: 'Map Maker', icon: <Map size={20} />, action: () => { } },
  'nav-blueprint': { id: 'nav-blueprint', label: 'Blueprint', icon: <SquareGanttChart size={20} />, action: () => { } },
  'nav-toolchain': { id: 'nav-toolchain', label: 'Toolchain', icon: <BookOpenText size={20} />, action: () => { } },
  'nav-advisor': { id: 'nav-advisor', label: 'Advisor', icon: <BookText size={20} />, action: () => { } },

  // Project-specific navigation (requires current project)
  'nav-files': { id: 'nav-files', label: 'Project Files', icon: <FileStack size={20} />, action: () => { } },
  'nav-kanban': { id: 'nav-kanban', label: 'Kanban Board', icon: <LayoutGrid size={20} />, action: () => { } },
  'nav-projectmap': { id: 'nav-projectmap', label: 'Project Map', icon: <Network size={20} />, action: () => { } },
};

// Helper function to parse the stored hotkey string
interface ParsedHotkey {
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
  key: string; // The main key (lowercase)
}

const parseHotkeyString = (hotkeyString: string): ParsedHotkey | null => {
  if (!hotkeyString) return null;
  const parts = hotkeyString.toLowerCase().split('+');
  const key = parts.pop(); // Last part is the main key
  if (!key) return null; // Invalid format

  return {
    ctrlKey: parts.includes('ctrl'),
    altKey: parts.includes('alt'),
    shiftKey: parts.includes('shift'),
    metaKey: parts.includes('meta'),
    key: key, // Already lowercase
  };
};

// New component to wrap content that needs router context
const AppContentWrapper = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const { currentProjectId } = useProjects();
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [blockReopen, setBlockReopen] = useState(false);
  const [isSelectionMenuVisible, setIsSelectionMenuVisible] = useState(false);
  const [isSettingsMenuVisible, setIsSettingsMenuVisible] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isAiAssistantVisible, setIsAiAssistantVisible] = useState(false);
  const [aiAssistantInitialPos, setAiAssistantInitialPos] = useState<{ x: number, y: number } | undefined>(undefined);
  const [aiAssistantSize, setAiAssistantSize] = useState<{ width: number, height: number } | undefined>(undefined); // Added state for AI assistant size

  // Auto-close global assistant when navigating to different routes (mobile only)
  useEffect(() => {
    if (isAiAssistantVisible && isMobile) {
      setIsAiAssistantVisible(false);
    }
  }, [location.pathname, isMobile]);

  // State to hold current wheel settings (hotkey + selected items)
  const [wheelSettings, setWheelSettings] = useState<WheelMenuSettingsData>(defaultWheelSettings);

  // State for the actual items displayed on the wheel
  const [menuItems, setMenuItems] = useState<WheelMenuItem[]>([]);

  const { openDialog } = useGlobalDialog();

  // Track project count changes for usage analytics
  useProjectCountTracking();

  // Determine current mode from location
  const getCurrentMode = () => {
    if (location.pathname.startsWith('/clientportal')) return 'client';
    if (location.pathname.startsWith('/manager')) return 'manager';
    return 'consultant';
  };
  const currentMode = getCurrentMode();

  // Determine if WheelMenu should be enabled on current page
  const isWheelMenuEnabled = () => {
    const pathname = location.pathname;

    // Block on landing pages
    if (['/landing', '/terms', '/privacy', '/about', '/pricing', '/contact', '/features', '/integrations'].includes(pathname)) {
      return false;
    }

    // Block on auth/onboarding pages
    if (['/auth', '/auth/callback', '/company-selection', '/role-selection', '/billing/return', '/data-deletion', '/app'].includes(pathname)) {
      return false;
    }

    // Block on analytics page
    if (pathname.startsWith('/admin')) {
      return false;
    }

    // Block on 404 page (but allow in protected routes)
    if (!pathname.startsWith('/consultant/') && !pathname.startsWith('/manager/') && !pathname.startsWith('/clientportal/')) {
      return false;
    }

    // Allow in protected routes, but respect client mode disable
    return currentMode !== 'client';
  };
  const wheelMenuEnabled = isWheelMenuEnabled();

  const handleCloseMenu = useCallback(() => {
    setMenuVisible(false);
  }, []);

  // Handle click away for toggle mode
  const handleClickAway = useCallback((event: MouseEvent) => {
    if (!wheelSettings.holdMode && menuVisible) {
      // In toggle mode, close menu when clicking away
      const target = event.target as HTMLElement;
      const wheelMenuContainer = document.querySelector('.wheel-menu-container');

      // Don't close if clicking inside the wheel menu
      if (wheelMenuContainer && wheelMenuContainer.contains(target)) {
        return;
      }

      handleCloseMenu();
    }
  }, [wheelSettings.holdMode, menuVisible, handleCloseMenu]);

  // Load initial settings based on auth state changes
  useEffect(() => {
    const loadSettingsForUser = async (userId: string) => {
      if (isLoadingSettings) {
        return;
      }

      try {
        setIsLoadingSettings(true);
        const { data, error } = await supabase
          .from('profiles')
          .select('wheel_hotkey, wheel_selection, wheel_hold_mode, ai_assistant_size') // Added wheel_hold_mode
          .eq('id', userId)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading wheel settings:', error);
          setWheelSettings(defaultWheelSettings);
          setAiAssistantSize(undefined); // Reset to default on error
        } else if (data) {
          const savedSelection = Array.isArray(data.wheel_selection)
            ? (data.wheel_selection as string[])
            : null;
          const selectionFromDb = savedSelection ?? defaultWheelSettings.selectedItemIds;

          // Sanitize selection to prevent stale/removed action IDs from causing warnings.
          const validActionIds = new Set(Object.keys(allWheelActions));
          let sanitizedSelection = selectionFromDb.filter((id) => validActionIds.has(id));
          const removedIds = selectionFromDb.filter((id) => !validActionIds.has(id));

          // If everything got removed (e.g., schema changed), fall back to default.
          if (sanitizedSelection.length === 0) {
            sanitizedSelection = defaultWheelSettings.selectedItemIds.filter((id) => validActionIds.has(id));
          }

          if (removedIds.length > 0) {
            console.warn('WheelMenu: removed unknown action IDs from saved selection', removedIds);
            // Best-effort: persist sanitized selection so the warning goes away permanently.
            supabase
              .from('profiles')
              .update({ wheel_selection: sanitizedSelection })
              .eq('id', userId)
              .then(({ error: updateError }) => {
                if (updateError) {
                  console.warn('WheelMenu: failed to persist sanitized selection', updateError);
                }
              });
          }

          const newSettings = {
            hotkey: data.wheel_hotkey || defaultWheelSettings.hotkey,
            selectedItemIds: sanitizedSelection,
            holdMode: data.wheel_hold_mode ?? defaultWheelSettings.holdMode,
          };
          setWheelSettings(newSettings);
          if (data.ai_assistant_size) { // Load AI assistant size
            setAiAssistantSize(data.ai_assistant_size as { width: number, height: number });
          }
        } else {
          // User has no specific selection or it's empty, use global default
          setWheelSettings(defaultWheelSettings);
          setAiAssistantSize(undefined); // Reset to default if no settings
        }
      } catch (err) {
        console.error('Load Settings: Unexpected error during loading:', err);
        setWheelSettings(defaultWheelSettings); // Fallback to global default
        setAiAssistantSize(undefined); // Reset to default on error
      } finally {
        setIsLoadingSettings(false);
      }
    };

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') {
        if (session?.user) {
          loadSettingsForUser(session.user.id);
        } else {
          setWheelSettings(defaultWheelSettings);
          setAiAssistantSize(undefined); // Reset to default on sign out
          if (isLoadingSettings) setIsLoadingSettings(false);
        }
      } else if (event === 'SIGNED_OUT') {
        setWheelSettings(defaultWheelSettings);
        setAiAssistantSize(undefined); // Reset to default on sign out
        if (isLoadingSettings) setIsLoadingSettings(false);
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  // Effect to update menuItems
  useEffect(() => {
    if (isLoadingSettings) {
      return;
    }
    const selectedIds = wheelSettings?.selectedItemIds || [];
    let items = selectedIds
      .map(id => {
        if (id === 'ai-assistant') {
          return {
            ...allWheelActions['ai-assistant'],
            action: () => {
              showAiAssistantAtPosition(menuPosition);
              handleCloseMenu();
            }
          };
        }
        if (id === 'settings') {
          return {
            ...allWheelActions['settings'],
            action: () => { }
          };
        }
        return allWheelActions[id];
      })
      .filter(item => {
        if (!item) {
          // This means `wheel_selection` contains an ID that is no longer present in `allWheelActions`.
          // We sanitize on load, but this also covers stale local state / races.
          console.warn('Derive MenuItems: No action found for provided ID', {
            missingId: selectedIds.find((id) => !allWheelActions[id]),
          });
        }
        return Boolean(item);
      });

    if (!selectedIds.includes('settings')) {
      items.push({
        id: 'settings',
        label: 'Wheel Settings',
        icon: <SettingsIcon size={20} />,
        action: () => { } // Action handled in handleMenuItemActivate
      });
    }
    setMenuItems(items);
  }, [wheelSettings, isLoadingSettings, menuPosition, handleCloseMenu, allWheelActions]);

  const handleMenuItemActivate = useCallback((item: WheelMenuItem) => {
    // Helper to handle actions and reset blockReopen after completion
    const performAction = (action: () => void) => {
      setBlockReopen(true);
      handleCloseMenu();
      // Reset blockReopen after a short delay to allow the action to complete
      setTimeout(() => setBlockReopen(false), 100);
      action();
    };

    if (item.id === 'settings') {
      performAction(() => setIsSettingsMenuVisible(true));
    } else if (item.id === 'ai-assistant') {
      // AI Assistant is handled differently - show the AI assistant centered
      performAction(() => {
        const event = new CustomEvent('showAiAssistantCentered');
        window.dispatchEvent(event);
      });
    } else if (item.id === 'event') {
      performAction(() => openDialog({ type: 'create-event' }));
    } else if (item.id === 'task') {
      performAction(() => openDialog({ type: 'new-task' }));
    } else if (item.id === 'note') {
      performAction(() => openDialog({ type: 'add-note' }));
    }
    // Navigation actions
    else if (item.id === 'nav-dashboard') {
      performAction(() => navigate(`/${currentMode}/dashboard`));
    } else if (item.id === 'nav-calendar') {
      performAction(() => navigate(`/${currentMode}/calendar`));
    } else if (item.id === 'nav-notes') {
      performAction(() => navigate(`/${currentMode}/notes`));
    } else if (item.id === 'nav-projects') {
      performAction(() => navigate(`/${currentMode}/projects`));
    } else if (item.id === 'nav-notifications') {
      performAction(() => navigate(`/${currentMode}/notifications`));
    } else if (item.id === 'nav-profile') {
      performAction(() => navigate(`/${currentMode}/profile`));
    } else if (item.id === 'nav-billing') {
      performAction(() => navigate(`/${currentMode}/billing`));
    }
    // Contacts & Messaging
    else if (item.id === 'nav-contacts') {
      performAction(() => navigate(`/${currentMode}/contacts`));
    } else if (item.id === 'nav-messages') {
      performAction(() => navigate(`/${currentMode}/unified-chat`));
    } else if (item.id === 'nav-email') {
      // Navigate to unified-chat with email filter/mode if supported
      performAction(() => navigate(`/${currentMode}/unified-chat?mode=email`));
    }
    // Research Tools - Navigate to specific research tabs
    else if (item.id === 'nav-chatbot') {
      performAction(() => navigate(`/${currentMode}/research/conversationalAI`));
    } else if (item.id === 'nav-insights') {
      performAction(() => navigate(`/${currentMode}/research/insights`));
    } else if (item.id === 'nav-dataframes') {
      performAction(() => navigate(`/${currentMode}/research/dataframes`));
    } else if (item.id === 'nav-speedreader') {
      performAction(() => navigate(`/${currentMode}/research/speedReader`));
    } else if (item.id === 'nav-mapmaker') {
      performAction(() => navigate(`/${currentMode}/research/scenarioModeler`));
    } else if (item.id === 'nav-blueprint') {
      performAction(() => navigate(`/${currentMode}/research/blueprint`));
    } else if (item.id === 'nav-toolchain') {
      performAction(() => navigate(`/${currentMode}/research/toolchain`));
    } else if (item.id === 'nav-advisor') {
      performAction(() => navigate(`/${currentMode}/research/advisor`));
    }
    // Project-specific navigation (requires current project)
    else if (item.id === 'nav-files') {
      performAction(() => {
        if (currentProjectId) {
          navigate(`/${currentMode}/project/${currentProjectId}/files`);
        } else {
          navigate(`/${currentMode}/projects`);
        }
      });
    } else if (item.id === 'nav-kanban') {
      performAction(() => {
        if (currentProjectId) {
          navigate(`/${currentMode}/project/${currentProjectId}/kanban`);
        } else {
          navigate(`/${currentMode}/projects`);
        }
      });
    } else if (item.id === 'nav-projectmap') {
      performAction(() => {
        if (currentProjectId) {
          navigate(`/${currentMode}/project/${currentProjectId}/map`);
        } else {
          navigate(`/${currentMode}/projects`);
        }
      });
    }
    // Default fallback
    else {
      console.log(`${item.label} activated`);
      performAction(() => { });
    }
  }, [handleCloseMenu, openDialog, navigate, currentMode, currentProjectId]);

  const handleSettingsSave = useCallback(async (newSettings: WheelMenuSettingsData) => {
    const { data: { user }, error: getUserError } = await supabase.auth.getUser();
    if (getUserError || !user) {
      console.error("Error fetching user or user not authenticated. Cannot save settings.", getUserError);
      return;
    }
    const userId = user.id;
    console.log(`Saving wheel settings for user ${userId}:`, newSettings);
    const previousSettings = { ...wheelSettings };

    // Optimistically update local state for wheel settings
    setWheelSettings(newSettings);

    // Reset blockReopen when settings are saved to ensure menu works immediately
    setBlockReopen(false);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          wheel_hotkey: newSettings.hotkey,
          wheel_selection: newSettings.selectedItemIds,
          wheel_hold_mode: newSettings.holdMode,
          // ai_assistant_size is NOT saved here, it's saved from GlobalAIAssistant
        })
        .eq('id', userId);
      if (updateError) {
        console.error("Error saving wheel menu settings:", updateError);
        setWheelSettings(previousSettings); // Revert on error
        // TODO: Show error toast
      } else {
        console.log("Wheel menu settings saved successfully.");
        // TODO: Maybe show a subtle success indicator?
      }
    } catch (err) {
      console.error("Unexpected error saving wheel settings:", err);
      setWheelSettings(previousSettings); // Revert on error
      // TODO: Show error toast
    }
  }, [wheelSettings]);

  // Callback to save AI Assistant size
  const handleAiAssistantResize = useCallback(async (newSize: { width: number; height: number }) => {
    setAiAssistantSize(newSize); // Optimistic update
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ ai_assistant_size: newSize })
          .eq('id', user.id);
        if (error) {
          console.error("Error saving AI assistant size:", error);
          // Potentially revert optimistic update or show toast
        } else {
          console.log("AI assistant size saved.")
        }
      } catch (err) {
        console.error("Unexpected error saving AI assistant size:", err);
      }
    }
  }, []);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  }, []);

  // Update handleKeyDown to use the parsed hotkey
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Prevent activation if WheelMenu is not enabled on current page
    if (!wheelMenuEnabled) {
      return;
    }

    // Prevent activation if the event target is an input, textarea, or contentEditable element
    const targetElement = event.target as HTMLElement;
    if (
      targetElement instanceof HTMLInputElement ||
      targetElement instanceof HTMLTextAreaElement ||
      targetElement.isContentEditable
    ) {
      return;
    }

    if (isLoadingSettings || blockReopen) return;

    const parsedHotkey = parseHotkeyString(wheelSettings.hotkey);
    if (!parsedHotkey) return; // Don't proceed if hotkey isn't valid

    // Exact match: check if the pressed key matches AND 
    // all required modifiers are pressed AND 
    // no non-required modifiers are pressed.
    const keyMatch = event.key.toLowerCase() === parsedHotkey.key ||
      (parsedHotkey.key === 'space' && event.key === ' '); // Handle spacebar
    const ctrlMatch = event.ctrlKey === parsedHotkey.ctrlKey;
    const altMatch = event.altKey === parsedHotkey.altKey;
    const shiftMatch = event.shiftKey === parsedHotkey.shiftKey;
    const metaMatch = event.metaKey === parsedHotkey.metaKey;

    if (keyMatch && ctrlMatch && altMatch && shiftMatch && metaMatch) {
      // Prevent default browser behavior if the hotkey might conflict (e.g., Ctrl+S)
      event.preventDefault();

      if (wheelSettings.holdMode) {
        // Hold mode: only show if not already visible
        if (!menuVisible) {
          setMenuPosition({ x: mousePosition.x, y: mousePosition.y });
          setMenuVisible(true);
        }
      } else {
        // Toggle mode: toggle visibility
        if (menuVisible) {
          setMenuVisible(false);
        } else {
          setMenuPosition({ x: mousePosition.x, y: mousePosition.y });
          setMenuVisible(true);
        }
      }
    }
  }, [
    isLoadingSettings,
    menuVisible,
    blockReopen,
    wheelSettings.hotkey,
    wheelSettings.holdMode,
    mousePosition,
    wheelMenuEnabled
  ]); // Updated dependencies

  // Update handleKeyUp to use the parsed hotkey
  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (isLoadingSettings) return;

    const parsedHotkey = parseHotkeyString(wheelSettings.hotkey);
    if (!parsedHotkey) return;

    // Check if the released key is the main key part of the hotkey
    if (!event.key) return; // Guard against undefined event.key
    const releasedKey = event.key.toLowerCase();
    const mainHotkeyKey = parsedHotkey.key;

    if (releasedKey === mainHotkeyKey || (mainHotkeyKey === 'space' && event.key === ' ')) {
      if (wheelSettings.holdMode) {
        // Hold mode: close menu when key is released
        if (menuVisible) {
          handleCloseMenu();
        }
      }
      // In toggle mode, key release doesn't close the menu

      // Allow reopening immediately on key release.
      setBlockReopen(false);
    }
    // If only a modifier key is released, we don't reset blockReopen here.
    // It gets reset when the *main* key of the combo is released.

  }, [isLoadingSettings, wheelSettings.hotkey, wheelSettings.holdMode, menuVisible, handleCloseMenu]); // Updated dependencies

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('click', handleClickAway);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('click', handleClickAway);
    };
  }, [handleKeyDown, handleKeyUp, handleMouseMove, handleClickAway]);

  useEffect(() => {
    if (isLoadingSettings) {
      return;
    }
    // Update lastActiveConsultantProjectId when navigating to a consultant project page
    const consultantProjectMatch = location.pathname.match(/\/consultant\/project\/([^\/]+)/);
    if (consultantProjectMatch && consultantProjectMatch[1]) {
      const newProjectId = consultantProjectMatch[1];
      localStorage.setItem('lastActiveConsultantProjectId', newProjectId);
    }

    // Update lastActiveManagerProjectId when navigating to a manager project page
    const managerProjectMatch = location.pathname.match(/\/manager\/project\/([^\/]+)/);
    if (managerProjectMatch && managerProjectMatch[1]) {
      const newProjectId = managerProjectMatch[1];
      localStorage.setItem('lastActiveManagerProjectId', newProjectId);
    }
  }, [location.pathname, isLoadingSettings]);

  // Function to show AI Assistant in center of screen (for header menu)
  const showAiAssistantCentered = useCallback(() => {
    setAiAssistantInitialPos(undefined); // No specific position = center
    setIsAiAssistantVisible(true);
  }, []);

  // Function to show AI Assistant at specific position (for wheel menu)
  const showAiAssistantAtPosition = useCallback((position: { x: number, y: number }) => {
    setAiAssistantInitialPos(position);
    setIsAiAssistantVisible(true);
  }, []);

  return (
    <>
      {/* Global Iridescence Background - always visible across all pages */}
      <IridescenceBackground />

      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <Routes>
          {/* Smart root route - shows landing for web, redirects to auth for Tauri */}
          <Route path="/" element={<RootRedirect />} />

          {/* Landing and Public Routes */}
          <Route path="/landing" element={
            <LandingLayout>
              <Suspense fallback={<div />}>
                <Landing />
              </Suspense>
            </LandingLayout>
          } />
          <Route path="/terms" element={
            <LandingLayout>
              <Suspense fallback={<div />}>
                <Terms />
              </Suspense>
            </LandingLayout>
          } />
          <Route path="/privacy" element={
            <LandingLayout>
              <Suspense fallback={<div />}>
                <Privacy />
              </Suspense>
            </LandingLayout>
          } />
          <Route path="/about" element={
            <LandingLayout>
              <Suspense fallback={<div />}>
                <About />
              </Suspense>
            </LandingLayout>
          } />
          <Route path="/pricing" element={
            <LandingLayout>
              <Suspense fallback={<div />}>
                <Pricing />
              </Suspense>
            </LandingLayout>
          } />
          <Route path="/contact" element={
            <LandingLayout>
              <Suspense fallback={<div />}>
                <Contact />
              </Suspense>
            </LandingLayout>
          } />
          <Route path="/features" element={
            <LandingLayout>
              <Suspense fallback={<div />}>
                <Features />
              </Suspense>
            </LandingLayout>
          } />
          <Route path="/integrations" element={
            <LandingLayout>
              <Suspense fallback={<div />}>
                <Integrations />
              </Suspense>
            </LandingLayout>
          } />
          <Route path="/billing/return" element={<BillingReturn />} />
          <Route path="/data-deletion" element={<DataDeletion />} />

          {/* PWA Smart Redirect Route */}
          <Route path="/app" element={<PWARedirect />} />

          {/* Auth Routes */}
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/role-selection" element={<RoleSelection />} />
          <Route path="/business-type-selection" element={<BusinessTypeSelection />} />
          <Route path="/company-selection" element={<CompanySelection />} />

          <Route
            path="/consultant/*"
            element={
              <ProtectedRoute>
                <CompanyRedirect>
                  <AppLayout
                    mode="consultant"
                    isAiAssistantVisible={isAiAssistantVisible}
                    setIsAiAssistantVisible={setIsAiAssistantVisible}
                    showAiAssistantCentered={showAiAssistantCentered}
                  >
                    <Suspense fallback={<LoadingSpinner size="md" />}>
                      <Routes>
                        {/* Main navigation routes */}
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<Dashboard />} />
                        <Route path="projects" element={<ProjectManagement />} />
                        <Route path="create-project" element={<CreateProject />} />
                        <Route path="calendar" element={<Calendar />} />
                        <Route path="messages" element={<ProjectChat />} />
                        <Route path="unified-chat" element={<ProjectChat />} />
                        <Route path="contacts" element={<Contacts />} />
                        <Route path="notifications" element={<Notifications />} />
                        <Route path="notes" element={<NotesWorkspace />} />
                        <Route path="docs" element={<Docs />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="profile/:tab" element={<Profile />} />
                        <Route path="settings" element={<Profile />} />
                        <Route path="research" element={<Research />} />
                        <Route path="research/:tab" element={<Research />} />
                        <Route path="clients" element={<Clients />} />
                        <Route path="clients/:tab" element={<Clients />} />
                        <Route path="company" element={<CompanyManagement />} />
                        <Route path="billing" element={<Billing />} />
                        <Route path="usage" element={<UsageTracking />} />
                        {/* Project-specific routes */}
                        <Route path="project/:projectId/overview" element={<ProjectOverview />} />
                        <Route path="project/:projectId/map" element={<ProjectMapPage />} />
                        <Route path="project/:projectId/chat" element={<ProjectChat />} />
                        <Route path="project/:projectId/files" element={<ProjectFiles />} />
                        {/* Fallback route */}
                        <Route path="*" element={<Navigate to="/consultant/dashboard" replace />} />
                      </Routes>
                    </Suspense>
                    <GlobalAIAssistant
                      isVisible={isAiAssistantVisible}
                      onClose={() => setIsAiAssistantVisible(false)}
                      initialPosition={aiAssistantInitialPos}
                      initialSize={aiAssistantSize} // Pass loaded/default size
                      onResizeEnd={handleAiAssistantResize} // Pass callback to save size
                    />
                  </AppLayout>
                </CompanyRedirect>
              </ProtectedRoute>
            }
          />
          {/* Client Portal routes */}
          <Route
            path="/clientportal/*"
            element={
              <ProtectedRoute>
                <CompanyRedirect>
                  <AppLayout
                    mode="client"
                    isAiAssistantVisible={isAiAssistantVisible}
                    setIsAiAssistantVisible={setIsAiAssistantVisible}
                    showAiAssistantCentered={showAiAssistantCentered}
                  >
                    <Suspense fallback={<LoadingSpinner size="md" />}>
                      <Routes>
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<ClientPortalDashboard />} />
                        <Route path="calendar" element={<Calendar />} />
                        <Route path="files" element={<ProjectFiles />} />
                        <Route path="chat" element={<ClientPortalChat />} />
                        <Route path="billing" element={<Billing />} />
                        <Route path="usage" element={<UsageTracking />} />
                        <Route path="messages" element={<ProjectChat />} />
                        <Route path="unified-chat" element={<ProjectChat />} />
                        <Route path="contacts" element={<Contacts />} />
                        <Route path="notifications" element={<Notifications />} />
                        <Route path="notes" element={<NotesWorkspace />} />
                        <Route path="docs" element={<Docs />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="profile/:tab" element={<Profile />} />
                        <Route path="settings" element={<Profile />} />
                      </Routes>
                    </Suspense>
                  </AppLayout>
                </CompanyRedirect>
              </ProtectedRoute>
            }
          />
          {/* Manager routes */}
          <Route
            path="/manager/*"
            element={
              <ProtectedRoute>
                <CompanyRedirect>
                  <AppLayout
                    mode="manager"
                    isAiAssistantVisible={isAiAssistantVisible}
                    setIsAiAssistantVisible={setIsAiAssistantVisible}
                    showAiAssistantCentered={showAiAssistantCentered}
                  >
                    <Suspense fallback={<LoadingSpinner size="md" />}>
                      <Routes>
                        {/* Main navigation routes */}
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<ManagerDashboard />} />
                        <Route path="calendar" element={<Calendar />} />
                        <Route path="projects" element={<ManagerProjects />} />
                        <Route path="create-project" element={<CreateProject />} />
                        <Route path="research" element={<Research />} />
                        <Route path="research/:tab" element={<Research />} />
                        <Route path="clients" element={<Clients />} />
                        <Route path="clients/:tab" element={<Clients />} />
                        <Route path="company" element={<CompanyManagement />} />
                        <Route path="company/:tab" element={<CompanyManagement />} />
                        <Route path="messages" element={<ProjectChat />} />
                        <Route path="unified-chat" element={<ProjectChat />} />
                        <Route path="contacts" element={<Contacts />} />
                        <Route path="notifications" element={<Notifications />} />
                        <Route path="notes" element={<NotesWorkspace />} />
                        <Route path="docs" element={<Docs />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="profile/:tab" element={<Profile />} />
                        <Route path="settings" element={<Profile />} />
                        <Route path="billing" element={<Billing />} />
                        <Route path="usage" element={<UsageTracking />} />
                        {/* Project-specific routes */}
                        <Route path="project/:projectId/overview" element={<ProjectOverview />} />
                        <Route path="project/:projectId/map" element={<ProjectMapPage />} />
                        <Route path="project/:projectId/chat" element={<ProjectChat />} />
                        <Route path="project/:projectId/files" element={<ProjectFiles />} />
                        {/* Fallback route */}
                        <Route path="*" element={<Navigate to="/manager/dashboard" replace />} />
                      </Routes>
                    </Suspense>
                    <GlobalAIAssistant
                      isVisible={isAiAssistantVisible}
                      onClose={() => setIsAiAssistantVisible(false)}
                      initialPosition={aiAssistantInitialPos}
                      initialSize={aiAssistantSize} // Pass loaded/default size
                      onResizeEnd={handleAiAssistantResize} // Pass callback to save size
                    />
                  </AppLayout>
                </CompanyRedirect>
              </ProtectedRoute>
            }
          />

          {/* Business Portal Routes */}
          {/* Business Portal Routes */}
          <Route path="/business" element={<Navigate to="/business/other/dashboard" replace />} />
          <Route path="/business/:businessType/*" element={
            <ProtectedRoute>
              <CompanyRedirect>
                <AppLayout mode="business">
                  <Suspense fallback={<LoadingSpinner size="md" />}>
                    <Routes>
                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="dashboard" element={<BusinessDashboard />} />
                      <Route path="chat" element={<BusinessChat />} />
                      <Route path="chat/:conversationId" element={<BusinessChat />} />
                      <Route path="contacts" element={<BusinessContacts />} />
                      <Route path="docs" element={<Docs />} />
                      <Route path="appointments" element={<BusinessAppointments />} />
                      <Route path="reservations" element={<BusinessReservations />} />
                      <Route path="orders" element={<BusinessOrders />} />
                      <Route path="settings/chatbot" element={<Navigate to="general" replace />} />
                      <Route path="settings/chatbot/:tab" element={<BusinessChatbotSettings />} />
                      <Route path="client/:clientId" element={<BusinessClient />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="profile/:tab" element={<Profile />} />
                      <Route path="*" element={<Navigate to="dashboard" replace />} />
                    </Routes>
                  </Suspense>
                </AppLayout>
              </CompanyRedirect>
            </ProtectedRoute>
          } />

          {/* Admin routes */}
          <Route path="/admin" element={<Navigate to="/admin/companies" replace />} />
          <Route path="/admin/:tab" element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingSpinner size="md" />}>
                  <Admin />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          } />
          {/* Legacy routes - redirect to new admin page */}
          <Route path="/admin/business" element={<Navigate to="/admin/companies" replace />} />

          {/* 404 Catch-all route - must be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Toaster />
      {!isLoadingSettings && wheelMenuEnabled && (
        <WheelMenu
          isVisible={menuVisible}
          position={menuPosition}
          items={menuItems}
          onClose={handleCloseMenu}
          onItemActivate={handleMenuItemActivate}
        />
      )}
      {!isLoadingSettings && (
        <WheelMenuSettings
          isVisible={isSettingsMenuVisible}
          onClose={() => setIsSettingsMenuVisible(false)}
          currentSettings={wheelSettings}
          onSave={handleSettingsSave}
          allWheelActions={allWheelActions} // Pass allWheelActions here
        />
      )}
      <OnboardingTour />
    </>
  );
};

// Create a client with optimized configuration for faster startup
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Enable background refetching for better UX
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Prevent excessive refetching
      refetchInterval: false,
    },
    mutations: {
      retry: false, // Don't retry mutations by default
      // onError can be added here for global mutation error handling
    },
  },
});

const App = () => {
  // Set prerenderReady signal after all client-side initialization is complete
  // This ensures Netlify Prerender extracts HTML after all effects have run
  useEffect(() => {
    // Use requestAnimationFrame to wait for initial render, then setTimeout
    // to ensure all useEffect hooks in child components have completed
    let timer: NodeJS.Timeout | null = null;
    const rafId = requestAnimationFrame(() => {
      timer = setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.prerenderReady = true;
        }
      }, 100); // Small delay to ensure all client-only effects complete
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <NavigationProvider>
                <StatusProvider>
                  <CustomThemeProvider>
                    <PaymentProvider>
                      <ProjectsProvider>
                        <GlobalDialogProvider>
                          <OnboardingProvider>
                            <NetworkStatus />
                            <AppContentWrapper />
                            <Toaster />
                          </OnboardingProvider>
                        </GlobalDialogProvider>
                      </ProjectsProvider>
                    </PaymentProvider>
                  </CustomThemeProvider>
                </StatusProvider>
              </NavigationProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
